import moment from "moment-timezone";
import { useEffect, useMemo, useState } from "react";

import type { DeviceType } from "@/components/filters/DeviceTypeFilter";
import { useLatestRequestGuard } from "@/hooks/useLatestRequestGuard";
import { AnalyticsServiceV2, type SessionMetrics } from "@/services/features/AnalyticsServiceV2";
import type { Store } from "@/services/features/StoreService";
import {
  type DateRange,
  storePerformanceService,
  type TrendComparisonWindow,
} from "@/services/StorePerformanceService";
import { getBrowserTimezone } from "@/utils/storeTime";

export type SessionsChartMetric =
  | "sessions"
  | "requests"
  | "unitsPerSession"
  | "missedRequests"
  | "items";

export interface SessionsReportDay {
  date: string;
  sessions: number;
  requestSessions: number;
  requests: number;
  totalItems: number;
  missedRequests: number | null;
}

export interface SessionsReportData {
  current: SessionsReportDay[];
  comparison: SessionsReportDay[];
}

const emptyData: SessionsReportData = { current: [], comparison: [] };

const createDays = (range: DateRange, timezone: string) => {
  const start = moment(range.start).tz(timezone).startOf("day");
  const end = moment(range.end).tz(timezone).startOf("day");
  const days: string[] = [];

  for (const day = start.clone(); day.isSameOrBefore(end, "day"); day.add(1, "day")) {
    days.push(day.format("YYYY-MM-DD"));
  }

  return days;
};

const getSessionCount = (data: SessionMetrics | undefined, deviceType: DeviceType) =>
  deviceType === "IN_AISLE"
    ? (data?.totalSessionsIncludingNoItems ?? 0)
    : (data?.totalSessions ?? 0);

interface UseSessionsReportOptions {
  stores: Store[] | null;
  currentRange: DateRange;
  comparisonWindow: TrendComparisonWindow;
  anchorComparisonToWeekdays?: boolean;
  deviceType: DeviceType;
}

export const useSessionsReport = ({
  stores,
  currentRange,
  comparisonWindow,
  anchorComparisonToWeekdays = true,
  deviceType,
}: UseSessionsReportOptions) => {
  const browserTimezone = getBrowserTimezone();
  const beginBaseRequest = useLatestRequestGuard();
  const storeIdsKey =
    stores
      ?.map((store) => store.id)
      .sort((a, b) => a - b)
      .join(",") ?? "";
  const [data, setData] = useState<SessionsReportData>(emptyData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [completedRequestKey, setCompletedRequestKey] = useState<string | null>(null);
  const comparisonRange = useMemo(
    () =>
      storePerformanceService.getTrendComparisonRange(
        currentRange,
        comparisonWindow,
        browserTimezone,
        anchorComparisonToWeekdays,
      ),
    [anchorComparisonToWeekdays, browserTimezone, comparisonWindow, currentRange],
  );
  const startDate = storePerformanceService.formatDate(currentRange.start, browserTimezone);
  const endDate = storePerformanceService.formatDate(currentRange.end, browserTimezone);
  const hasRequestInputs = Boolean(storeIdsKey && startDate && endDate);
  const requestKey = hasRequestInputs
    ? `${storeIdsKey}:${startDate}:${endDate}:${comparisonWindow}:${anchorComparisonToWeekdays}:${deviceType}`
    : null;

  useEffect(() => {
    const isCurrent = beginBaseRequest();
    const storeIds = storeIdsKey ? storeIdsKey.split(",").map(Number) : [];

    if (!storeIds.length || !startDate || !endDate) {
      setData(emptyData);
      setLoading(false);
      setError(false);
      setCompletedRequestKey(null);
      return;
    }

    const analytics = AnalyticsServiceV2.getInstance();
    const loadRange = async (range: DateRange): Promise<SessionsReportDay[]> => {
      const days = createDays(range, browserTimezone);
      const baseParams = {
        storeIds,
        startDate: storePerformanceService.formatDate(range.start, browserTimezone),
        endDate: storePerformanceService.formatDate(range.end, browserTimezone),
        ...(deviceType === "all" ? {} : { deviceType }),
      };
      const sessionResponse = await analytics.getDailySessionAnalytics(baseParams);
      const requestResponse = await analytics.getDailyRequestAnalytics(baseParams);
      const sessionsByDate = new Map(
        (sessionResponse.data ?? []).map((session) => [session.date, session]),
      );
      const requestsByDate = new Map(
        (requestResponse.data ?? []).map((request) => [request.date, request]),
      );

      return days.map((date) => {
        const session = sessionsByDate.get(date);
        const request = requestsByDate.get(date);

        return {
          date,
          sessions: getSessionCount(session, deviceType),
          requestSessions: request?.totalShoppers ?? 0,
          requests: request?.totalRequests ?? 0,
          totalItems: session?.totalItems ?? 0,
          missedRequests: request?.totalMissedRequests ?? 0,
        };
      });
    };

    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        const current = await loadRange(currentRange);
        const comparison = await loadRange(comparisonRange);
        if (isCurrent()) {
          setData({ current, comparison });
          setCompletedRequestKey(requestKey);
        }
      } catch (loadError) {
        console.error("Failed to load sessions report:", loadError);
        if (isCurrent()) {
          setData(emptyData);
          setError(true);
          setCompletedRequestKey(requestKey);
        }
      } finally {
        if (isCurrent()) setLoading(false);
      }
    };

    void load();
  }, [
    beginBaseRequest,
    browserTimezone,
    comparisonRange,
    currentRange,
    deviceType,
    endDate,
    requestKey,
    startDate,
    storeIdsKey,
  ]);

  const isRequestPending = hasRequestInputs && completedRequestKey !== requestKey;

  return {
    comparisonRange,
    data,
    error: error && completedRequestKey === requestKey,
    loading: loading || isRequestPending,
  };
};
